import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { ApplicationContext } from '../../../../shared/source/context/context';
import { userSettingsSchema } from '../../../../shared/source/data-contract/user-settings-schema';

import type { UserSettings } from '../../../../shared/source/data-contract/user-settings-schema';
import type { Pathname } from '../../../../shared/source/http-fetcher/http-fetcher';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

const pathname: Pathname = '/users/me/settings';
const settingsQueryKey = [pathname, 'GET'];

export const useUserSettingsQuery = <K extends keyof UserSettings>(
    userSettingsKey: K,
): Readonly<Record<`${K}Query`, UseQueryResult>> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const userSettingsQuery = useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(userSettingsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: settingsQueryKey,
        select: (data: UserSettings) => data[userSettingsKey],
    });
    const selectedSetting = `${userSettingsKey}Query` as const;

    return {
        [selectedSetting]: userSettingsQuery,
    } as Readonly<Record<typeof selectedSetting, typeof userSettingsQuery>>;
};

export const useUserSettingsMutation = (): UseMutationResult<UserSettings, Error, Partial<UserSettings>> => {
    const queryClient = useQueryClient();
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useMutation({
        mutationFn: async (data) => {
            const result = await validatingCobrainerApiFetcher.patchJson(userSettingsSchema, {
                pathname,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }
            throw result.error;
        },
        onError: () => queryClient.invalidateQueries({ queryKey: settingsQueryKey }),
        onSuccess: (settings: UserSettings) => {
            queryClient.setQueryData(settingsQueryKey, settings);
        },
    });
};
